import type { AxiosInstance, AxiosResponse } from "axios";
import type {
  BiometryValidityParamsResponseDTO,
  BioTokenIdReferenceResponseDTO,
  InvoiceDeliveryTypesResponseDTO,
  NameGenderResponseDTO,
  NeedNewBiometryResponseDTO,
  PersonalRelationshipTypesResponseDTO,
  ProposalPhoneTokenStatusResponseDTO,
  ProposalAnalysisResultResponseDTO,
  UnicoCaptureMaxAttemptsResponseDTO,
  UnicoStagesPerformedResponseDTO,
  UpdateProposalPhoneTokenStatusParamsDTO,
  UserPermissionsResponseDTO,
  WayupProposalDataResponseDTO,
  WayupProposalSearchResponseDTO
} from "@repo/core/services/entities/WayupRequisitions";

const resourcePrefix = "/requisicoes";

export class RequisitionsResource {
  apiProvider: AxiosInstance;

  constructor(instance: AxiosInstance) {
    this.apiProvider = instance;
  }

  getBiometryValidityParams(
    vargrupo: string
  ): Promise<AxiosResponse<BiometryValidityParamsResponseDTO>> {
    return this.apiProvider.get(`${resourcePrefix}/busca-prazo-validade-bio`, {
      params: { vargrupo }
    });
  }

  getUnicoStagesPerformed(
    varproposta: string | number,
    limiteMinutos: number
  ): Promise<AxiosResponse<UnicoStagesPerformedResponseDTO>> {
    return this.apiProvider.get(
      `${resourcePrefix}/busca-unico-etapas-realizadas`,
      {
        params: {
          varproposta: String(varproposta),
          limite_minutos: limiteMinutos
        }
      }
    );
  }

  getUserPermissions(): Promise<AxiosResponse<UserPermissionsResponseDTO>> {
    return this.apiProvider.get(`${resourcePrefix}/busca-permissoes-usuario`);
  }

  getNameGender(
    nome: string
  ): Promise<AxiosResponse<NameGenderResponseDTO>> {
    return this.apiProvider.get(`${resourcePrefix}/busca-genero-nome`, {
      params: { nome },
      timeout: 5_000
    });
  }

  getUnicoCaptureMaxAttempts(): Promise<
    AxiosResponse<UnicoCaptureMaxAttemptsResponseDTO>
  > {
    return this.apiProvider.get(`${resourcePrefix}/busca-qtde-vezes-via-unico`);
  }

  getNeedNewBiometry(
    proposta: string | number
  ): Promise<AxiosResponse<NeedNewBiometryResponseDTO>> {
    return this.apiProvider.get(
      `${resourcePrefix}/busca-necessidade-nova-bio`,
      {
        params: { proposta: String(proposta) }
      }
    );
  }

  /**
   * Latest completed process id for Unico 1:1 (idtoken) bioTokenId.
   * Query `cliente` must match ERP convention: digits-only CPF + trailing "1".
   */
  getBioTokenIdReference(
    strClienteDigits: string
  ): Promise<AxiosResponse<BioTokenIdReferenceResponseDTO>> {
    const cliente = `${strClienteDigits}1`;
    return this.apiProvider.get(
      `${resourcePrefix}/busca-biotokenid-referencia`,
      {
        params: { cliente }
      }
    );
  }

  getProposalAnalysisResult(
    proposta: string
  ): Promise<AxiosResponse<ProposalAnalysisResultResponseDTO>> {
    return this.apiProvider.get(
      `${resourcePrefix}/busca-resultado-analise-proposta`,
      {
        params: { proposta }
      }
    );
  }

  getInvoiceDeliveryTypes(
    strproduto: string
  ): Promise<AxiosResponse<InvoiceDeliveryTypesResponseDTO>> {
    return this.apiProvider.get(`${resourcePrefix}/busca-tipo-envio-fatura`, {
      params: { strproduto }
    });
  }

  getPersonalRelationshipTypes(): Promise<
    AxiosResponse<PersonalRelationshipTypesResponseDTO>
  > {
    return this.apiProvider.get(`${resourcePrefix}/busca-tipos-relac-pessoal`);
  }

  getProposalData(
    varproposta: string | number
  ): Promise<AxiosResponse<WayupProposalDataResponseDTO>> {
    return this.apiProvider.get(`${resourcePrefix}/busca-dados-proposta`, {
      params: { varproposta: String(varproposta) }
    });
  }

  searchProposalsByCpf(params: {
    strprodutos: string;
    strbusca: string;
  }): Promise<AxiosResponse<WayupProposalSearchResponseDTO>> {
    return this.apiProvider.get(
      `${resourcePrefix}/busca-dados-proposta-via-buscar`,
      { params }
    );
  }

  getProposalPhoneTokenStatus(
    varproposta: string | number
  ): Promise<AxiosResponse<ProposalPhoneTokenStatusResponseDTO>> {
    return this.apiProvider.get(
      `${resourcePrefix}/busca-status-token-fone-proposta`,
      {
        params: { varproposta: String(varproposta) }
      }
    );
  }

  updateProposalPhoneTokenStatus(
    params: UpdateProposalPhoneTokenStatusParamsDTO
  ): Promise<AxiosResponse<ProposalPhoneTokenStatusResponseDTO>> {
    return this.apiProvider.put(
      `${resourcePrefix}/grava-validacao-token-proposta`,
      undefined,
      { params }
    );
  }
}
